import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {studentSignUpSchema} from '../../../lib/zodUtils';
import {useSignupStudentMutation} from '../../../store/api/authApi';
import {updateBasicInfo, updateSignUpData} from '../../../store/slices/studentSlice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetSchoolsQuery } from '../../../store/api/masterApi';
import { RootState } from '../../../store/store';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  grade: string;
  school: string;
  terms: boolean;
}

export const useStudentSignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [signupStudent, {isLoading, error}] = useSignupStudentMutation();
  const { languages, grades } = useSelector((s: RootState) => s.master);
  const { data: schools, isLoading: isSchoolsLoading } = useGetSchoolsQuery();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    grade: '',
    school: '',
    terms: false
  });

  function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T | undefined>(undefined);
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const prevTerms = usePrevious(formData.terms);
  const formatPhoneNumber = (value: string): string => {
    const digitsOnly = value.replace(/\D/g, '');
    const trimmedDigits = digitsOnly.slice(0, 10);

    if (trimmedDigits.length <= 3) {
      return trimmedDigits;
    } else if (trimmedDigits.length <= 6) {
      return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3)}`;
    } else {
      return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3, 6)}-${trimmedDigits.slice(
        6,
      )}`;
    }
  };

  const updateField = (field: keyof FormData) => (value: any) => {
    let updatedValue = value;

    if (field === 'phone') {
      updatedValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({...prev, [field]: updatedValue}));
    if (errors[field]) {
      setErrors(prev => ({...prev, [field]: undefined}));
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    const validationResult = studentSignUpSchema.safeParse(formData);

    if (validationResult.success) {
      const signUpData = {
        userType: 'student' as const,
        userCreds: {
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
        },
        basicInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          grade: formData.grade,
          school: formData.school,
          parentid: 0, 
        },
      };

      try {
        const response = await signupStudent(signUpData).unwrap(); 

        dispatch(
          updateSignUpData({
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
            userType: 'student',
          }),
        );
        dispatch(
          updateBasicInfo({
            firstName: formData.firstName,
            lastName: formData.lastName,
            grade: formData.grade,
            school: formData.school,
            parentid: 0,
          }),
        );

        console.log('Student Signup Success:', response);
        navigate('/student-onboarding-complete');
      } catch (err) {
        console.error('Student Signup Error:', err);
      }
    } else {
      const formattedErrors = Object.fromEntries(
        Object.entries(validationResult.error.flatten().fieldErrors).map(([key, value]) => [
          key,
          value[0],
        ]),
      ) as Partial<Record<keyof FormData, string>>;
      setErrors(formattedErrors);
      console.log('Validation Errors:', formattedErrors);
    }
  };
  const schoolDropdownData = useMemo(() => {
    return (schools || []).map(item => ({
    label: item.name,
    value: item.id.toString(),
    }));
  }, [schools]);

  const gradeDropdownData = useMemo(() => {
    return grades.map(item => ({
    label: item.name,
    value: item.id.toString(),
    }));
  }, [grades]);
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    password: formData.password,
    phone: formData.phone,
    grade: formData.grade,
    school: formData.school,
    errors,
    isLoading, 
    setFirstName: updateField('firstName'),
    setLastName: updateField('lastName'),
    setEmail: updateField('email'),
    setPassword: updateField('password'),
    setPhone: updateField('phone'),
    setGrade: updateField('grade'),
    setSchool: updateField('school'),
    handleSubmit,
    schoolDropdownData,
    gradeDropdownData,
    terms: formData.terms,
    setTerms: updateField('terms'),
  };
};
